import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useAuthStore } from '../../store/auth-store';
import {
  CrudDrawer,
  TextField,
  NumberField,
  CheckField,
  Field,
  DeleteButton,
  extractErr,
} from '../../components/crud-drawer';

// Colours the seller product form offers. Previously a 15-item `static const`
// in seller_add_product_page.dart, so adding one meant an app release.
//
// The hex is what makes this a table rather than a master_lookups row: the
// picker paints a swatch, and the catalog colour facet will too.

interface ProductColor {
  id: string;
  name: string;
  slug: string;
  hex: string;
  sortOrder: number;
  isActive: boolean;
}

function slugify(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const EMPTY = { name: '', slug: '', hex: '#000000', sortOrder: '', isActive: true };

export function ColorsTab() {
  const canWrite = useAuthStore((s) => s.hasScope)('master.write');

  const [rows, setRows] = useState<ProductColor[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ProductColor | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const { confirm } = useConfirm();
  // Client-side: the API returns every colour. Matches what each card shows.
  const { query, setQuery, filtered } = useTableSearch(rows, (r) => [
    r.name, r.hex, r.slug, r.isActive ? 'Active' : 'Inactive',
  ]);
  const [slugTouched, setSlugTouched] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<ProductColor[]>('/admin/product-colors');
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null); setCreating(true); setSlugTouched(false); setForm(EMPTY);
  };

  const openEdit = (row: ProductColor) => {
    setCreating(false); setEditing(row); setSlugTouched(true);
    setForm({
      name: row.name,
      slug: row.slug,
      hex: row.hex,
      sortOrder: String(row.sortOrder),
      isActive: row.isActive,
    });
  };

  const close = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!/^#[0-9A-Fa-f]{6}$/.test(form.hex)) { toast.error('Hex must look like #RRGGBB'); return; }
    const slug = (form.slug || slugify(form.name)).trim();
    const body = {
      name: form.name.trim(),
      slug,
      hex: form.hex.toUpperCase(),
      ...(form.sortOrder !== '' ? { sortOrder: Number(form.sortOrder) } : {}),
      isActive: form.isActive,
    };
    setBusy(true);
    try {
      if (creating) {
        await api.post('/admin/product-colors', body);
        toast.success('Colour added');
      } else if (editing) {
        await api.put(`/admin/product-colors/${editing.id}`, body);
        toast.success('Colour updated');
      }
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    const ok = await confirm({
      title: `Delete "${editing.name}"?`,
      description: 'This can\'t be undone, and it fails if any product variant uses the colour. Deactivate it instead to remove it from the seller picker.',
      confirmLabel: 'Delete colour',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/product-colors/${editing.id}`);
      toast.success('Colour deleted');
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row: ProductColor, next: boolean) => {
    // Switching off hides it at once, so ask first; switching back on is
    // harmless and stays one click.
    if (!next) {
      const ok = await confirm({
        title: `Deactivate "${row.name}"?`,
        description: 'It vanishes from the seller\'s variant picker; products already using it keep their colour. You can switch it back on any time.',
        confirmLabel: 'Deactivate colour',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await api.put(`/admin/product-colors/${row.id}`, { isActive: next });
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: !next } : r)));
    }
  };

  return (
    <>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-surface-line px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-ink-900">Colours</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-500">
              What a seller can pick per product variant. The swatch is painted from the hex.
            </p>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={openCreate}
              className="btn btn-primary btn-sm shrink-0"
            >
              <Symbol name="add" size={16} /> Add colour
            </button>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="border-b border-surface-line px-5 py-3">
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search by colour name, hex or status"
              resultCount={filtered.length}
            />
          </div>
        )}

        {loading ? (
          <SkeletonRows cols={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="palette"
            title="No colours yet"
            body="Colours you add here become available in the seller's variant picker."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for "${query.trim()}"`}
            body="Try a colour name, a hex code like #8B0000, or Active / Inactive."
            action={
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>
                Clear search
              </button>
            }
          />
        ) : (
          <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <div
                key={r.id}
                className={`card flex items-center gap-3 p-3 ${
                  r.isActive ? '' : 'opacity-60'
                }`}
              >
                <span
                  className="h-9 w-9 shrink-0 rounded-sm border border-ink-200"
                  style={{ backgroundColor: r.hex }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink-900">{r.name}</div>
                  <div className="sku text-ink-500">{r.hex}</div>
                </div>
                <Toggle
                  checked={r.isActive}
                  disabled={!canWrite}
                  aria-label={`${r.isActive ? 'Deactivate' : 'Activate'} ${r.name}`}
                  onChange={(v) => void toggleActive(r, v)}
                />
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => openEdit(r)}
                    aria-label={`Edit ${r.name}`}
                    className="btn btn-quiet btn-icon btn-sm shrink-0"
                  >
                    <Symbol name="edit" size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <CrudDrawer
        open={creating || editing !== null}
        title={creating ? 'New colour' : `Edit ${editing?.name ?? ''}`}
        busy={busy}
        onClose={close}
        onSubmit={() => void save()}
        secondaryAction={editing ? <DeleteButton onClick={() => void remove()} disabled={busy} /> : undefined}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={(v) => setForm((f) => ({ ...f, name: v, slug: slugTouched ? f.slug : slugify(v) }))}
          placeholder="Mustard"
          required
          hint={
            editing
              ? 'Variants reference a colour by name, so the API blocks renaming one already in use.'
              : undefined
          }
        />
        <TextField
          label="Slug"
          value={form.slug}
          onChange={(v) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: v })); }}
          placeholder="mustard"
          mono
          required
        />
        <Field label="Swatch" required hint="Shown in the seller's colour picker and the shopper's filter.">
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={/^#[0-9A-Fa-f]{6}$/.test(form.hex) ? form.hex : '#000000'}
              onChange={(e) => setForm((f) => ({ ...f, hex: e.target.value.toUpperCase() }))}
              className="h-[42px] w-14 shrink-0 cursor-pointer rounded-sm border border-surface-line-strong bg-surface-card p-1"
            />
            <input
              type="text"
              value={form.hex}
              onChange={(e) => setForm((f) => ({ ...f, hex: e.target.value.toUpperCase() }))}
              placeholder="#RRGGBB"
              maxLength={7}
              className="input font-mono"
            />
          </div>
        </Field>
        <NumberField
          label="Sort order"
          value={form.sortOrder}
          onChange={(v) => setForm((f) => ({ ...f, sortOrder: v }))}
          placeholder="0"
          min={0}
        />
        <CheckField
          label="Active"
          hint="Inactive colours vanish from the seller picker; products already using them keep their colour."
          checked={form.isActive}
          onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
        />
      </CrudDrawer>
    </>
  );
}
